import React, {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  forwardRef,
} from 'react';
import { Icon } from '@iconify/react';
import {
  DEFAULT_AZIMUTH,
  DEFAULT_ELEVATION,
  DEPTH_PULL_NDC,
  FLOOR_CELL_M,
  FLOOR_COLOR_A,
  FLOOR_COLOR_B,
  FLOOR_Z,
  JOINT_BASE_COLOR,
  JOINT_SPECULAR_POWER,
  cameraEye,
  cameraTarget,
  clampElevation,
  fitDistance,
  orbitDrag,
  panTarget,
  planeViewAzimuth,
  skeletonMarkSizes,
  torsoAxes,
  viewProjectionMatrix,
  writeBoneQuad,
  zoomDistance,
} from '../../../util/trialLiveLayout';

const FLOOR_VS = `#version 300 es
layout(location=0) in vec3 a_pos;
uniform mat4 u_vp;
out vec3 v_world;
void main() {
  v_world = a_pos;
  gl_Position = u_vp * vec4(a_pos, 1.0);
}`;

const FLOOR_FS = `#version 300 es
precision mediump float;
in vec3 v_world;
uniform float u_cell;
uniform vec3 u_c0;
uniform vec3 u_c1;
out vec4 frag;
void main() {
  float ix = floor(v_world.x / u_cell + 1000.0);
  float iy = floor(v_world.y / u_cell + 1000.0);
  float checker = mod(ix + iy, 2.0);
  frag = vec4(checker < 0.5 ? u_c0 : u_c1, 1.0);
}`;

const BONE_VS = `#version 300 es
layout(location=0) in vec3 a_ndc;
layout(location=1) in vec3 a_color;
out vec3 v_color;
void main() {
  v_color = a_color;
  gl_Position = vec4(a_ndc, 1.0);
}`;

const BONE_FS = `#version 300 es
precision mediump float;
in vec3 v_color;
out vec4 frag;
void main() {
  frag = vec4(v_color, 1.0);
}`;

const JOINT_VS = `#version 300 es
layout(location=0) in vec3 a_pos;
uniform mat4 u_vp;
uniform float u_size;
void main() {
  gl_Position = u_vp * vec4(a_pos, 1.0);
  gl_PointSize = u_size;
}`;

const JOINT_FS = `#version 300 es
precision mediump float;
uniform vec3 u_base;
uniform float u_power;
uniform float u_pull;
out vec4 frag;
void main() {
  vec2 p = gl_PointCoord * 2.0 - 1.0;
  float r2 = dot(p, p);
  if (r2 > 1.0) discard;
  vec3 n = vec3(p, sqrt(max(0.0, 1.0 - r2)));
  vec3 light = normalize(vec3(0.35, 0.45, 0.82));
  float lambert = max(dot(n, light), 0.0);
  vec3 halfDir = normalize(light + vec3(0.0, 0.0, 1.0));
  float spec = pow(max(dot(n, halfDir), 0.0), u_power);
  vec3 color = u_base * (0.25 + 0.75 * lambert) + vec3(spec);
  frag = vec4(color, 1.0);
  gl_FragDepth = gl_FragCoord.z - u_pull * 0.5;
}`;

function compile(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

function link(gl, vsSource, fsSource) {
  const vs = compile(gl, gl.VERTEX_SHADER, vsSource);
  const fs = compile(gl, gl.FRAGMENT_SHADER, fsSource);
  if (!vs || !fs) return null;
  const program = gl.createProgram();
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    gl.deleteProgram(program);
    return null;
  }
  return program;
}

function createRenderer(canvas) {
  let gl = null;
  try {
    gl = canvas.getContext('webgl2', {
      antialias: true,
      alpha: false,
      depth: true,
    });
  } catch (err) {
    gl = null;
  }
  if (!gl) return null;
  const floorProgram = link(gl, FLOOR_VS, FLOOR_FS);
  const boneProgram = link(gl, BONE_VS, BONE_FS);
  const jointProgram = link(gl, JOINT_VS, JOINT_FS);
  if (!floorProgram || !boneProgram || !jointProgram) return null;

  const floorBuffer = gl.createBuffer();
  const boneBuffer = gl.createBuffer();
  const jointBuffer = gl.createBuffer();
  const boneData = new Float32Array(4096 * 6);
  const jointData = new Float32Array(256 * 3);
  const quadScratch = new Float32Array(18);
  const floorVerts = new Float32Array(18);
  const endA = { x: 0, y: 0, z: 0, wx: 0, wy: 0, wz: 0 };
  const endB = { x: 0, y: 0, z: 0, wx: 0, wy: 0, wz: 0 };
  const white = [1, 1, 1];
  const floorVpLoc = gl.getUniformLocation(floorProgram, 'u_vp');
  const floorCellLoc = gl.getUniformLocation(floorProgram, 'u_cell');
  const floorC0Loc = gl.getUniformLocation(floorProgram, 'u_c0');
  const floorC1Loc = gl.getUniformLocation(floorProgram, 'u_c1');
  const jointVpLoc = gl.getUniformLocation(jointProgram, 'u_vp');
  const jointSizeLoc = gl.getUniformLocation(jointProgram, 'u_size');
  const jointBaseLoc = gl.getUniformLocation(jointProgram, 'u_base');
  const jointPowerLoc = gl.getUniformLocation(jointProgram, 'u_power');
  const jointPullLoc = gl.getUniformLocation(jointProgram, 'u_pull');
  let sizeDirty = true;
  let cssWidth = 1;
  let cssHeight = 1;
  let dpr = 1;
  let floorKey = '';
  let boneCapacity = 0;
  let jointCapacity = 0;

  function measure() {
    cssWidth = Math.max(1, canvas.clientWidth);
    cssHeight = Math.max(1, canvas.clientHeight);
    dpr = window.devicePixelRatio || 1;
    const width = Math.max(1, Math.round(cssWidth * dpr));
    const height = Math.max(1, Math.round(cssHeight * dpr));
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
  }

  function projectJoint(vp, pose, frame, joint, jointCount, dest) {
    if (!pose || frame < 0 || joint < 0) return false;
    const o = (frame * jointCount + joint) * 3;
    if (o < 0 || o + 2 >= pose.length) return false;
    const x = pose[o];
    const y = pose[o + 1];
    const z = pose[o + 2];
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return false;
    const cx = vp[0] * x + vp[4] * y + vp[8] * z + vp[12];
    const cy = vp[1] * x + vp[5] * y + vp[9] * z + vp[13];
    const cz = vp[2] * x + vp[6] * y + vp[10] * z + vp[14];
    const cw = vp[3] * x + vp[7] * y + vp[11] * z + vp[15];
    if (!Number.isFinite(cw) || cw <= 1e-6) return false;
    const inv = 1 / cw;
    dest.x = (cx * inv * 0.5 + 0.5) * cssWidth;
    dest.y = (cy * inv * -0.5 + 0.5) * cssHeight;
    dest.z = cz * inv;
    dest.wx = x;
    dest.wy = y;
    dest.wz = z;
    return true;
  }

  function uploadFloor(floor) {
    const key = `${floor.minX}|${floor.minY}|${floor.maxX}|${floor.maxY}`;
    if (key === floorKey) return;
    floorKey = key;
    const z = FLOOR_Z;
    floorVerts[0] = floor.minX;
    floorVerts[1] = floor.minY;
    floorVerts[2] = z;
    floorVerts[3] = floor.maxX;
    floorVerts[4] = floor.minY;
    floorVerts[5] = z;
    floorVerts[6] = floor.maxX;
    floorVerts[7] = floor.maxY;
    floorVerts[8] = z;
    floorVerts[9] = floor.minX;
    floorVerts[10] = floor.minY;
    floorVerts[11] = z;
    floorVerts[12] = floor.maxX;
    floorVerts[13] = floor.maxY;
    floorVerts[14] = z;
    floorVerts[15] = floor.minX;
    floorVerts[16] = floor.maxY;
    floorVerts[17] = z;
    gl.bindBuffer(gl.ARRAY_BUFFER, floorBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, floorVerts, gl.STATIC_DRAW);
  }

  function draw(frame, scene) {
    if (sizeDirty) {
      measure();
      sizeDirty = false;
    }
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.clearColor(1, 1, 1, 1);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.enable(gl.DEPTH_TEST);
    gl.disable(gl.BLEND);

    const target = cameraTarget(scene.floor, scene.pan);
    const eye = cameraEye(target, scene.azimuth, scene.elevation, scene.distance);
    const vp = viewProjectionMatrix(eye, target, cssWidth / cssHeight);
    const { floor } = scene;
    const marks = skeletonMarkSizes(scene.distance, cssHeight);

    uploadFloor(floor);
    gl.useProgram(floorProgram);
    gl.uniformMatrix4fv(floorVpLoc, false, vp);
    gl.uniform1f(floorCellLoc, FLOOR_CELL_M);
    gl.uniform3fv(floorC0Loc, FLOOR_COLOR_A);
    gl.uniform3fv(floorC1Loc, FLOOR_COLOR_B);
    gl.bindBuffer(gl.ARRAY_BUFFER, floorBuffer);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 0, 0);
    gl.drawArrays(gl.TRIANGLES, 0, 6);

    let boneFloats = 0;
    const { pose, jointCount, bones, boneColors } = scene;
    for (let i = 0; i < bones.length; i += 1) {
      const aOk = projectJoint(vp, pose, frame, bones[i][0], jointCount, endA);
      const bOk = projectJoint(vp, pose, frame, bones[i][1], jointCount, endB);
      if (!aOk || !bOk) continue;
      const written = writeBoneQuad(
        quadScratch,
        0,
        endA.x,
        endA.y,
        endB.x,
        endB.y,
        endA.z,
        endB.z,
        cssWidth,
        cssHeight,
        marks.boneThicknessCss,
        marks.jointRadiusCss
      );
      if (written !== 18) continue;
      if (boneFloats + 36 > boneData.length) break;
      const color = boneColors[i] || white;
      for (let v = 0; v < 6; v += 1) {
        const s = v * 3;
        const o = boneFloats + v * 6;
        boneData[o] = quadScratch[s];
        boneData[o + 1] = quadScratch[s + 1];
        boneData[o + 2] = quadScratch[s + 2];
        boneData[o + 3] = color[0];
        boneData[o + 4] = color[1];
        boneData[o + 5] = color[2];
      }
      boneFloats += 36;
    }
    if (boneFloats > 0) {
      gl.useProgram(boneProgram);
      gl.bindBuffer(gl.ARRAY_BUFFER, boneBuffer);
      if (boneCapacity === 0) {
        gl.bufferData(gl.ARRAY_BUFFER, boneData, gl.DYNAMIC_DRAW);
        boneCapacity = boneData.length;
      }
      gl.bufferSubData(gl.ARRAY_BUFFER, 0, boneData, 0, boneFloats);
      gl.enableVertexAttribArray(0);
      gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 24, 0);
      gl.enableVertexAttribArray(1);
      gl.vertexAttribPointer(1, 3, gl.FLOAT, false, 24, 12);
      gl.drawArrays(gl.TRIANGLES, 0, boneFloats / 6);
    }

    let jointFloats = 0;
    for (let j = 0; j < jointCount; j += 1) {
      if (!projectJoint(vp, pose, frame, j, jointCount, endA)) continue;
      if (jointFloats + 3 > jointData.length) break;
      jointData[jointFloats] = endA.wx;
      jointData[jointFloats + 1] = endA.wy;
      jointData[jointFloats + 2] = endA.wz;
      jointFloats += 3;
    }
    if (jointFloats > 0) {
      gl.useProgram(jointProgram);
      gl.uniformMatrix4fv(jointVpLoc, false, vp);
      gl.uniform1f(jointSizeLoc, marks.jointDiameterCss * dpr);
      gl.uniform3fv(jointBaseLoc, JOINT_BASE_COLOR);
      gl.uniform1f(jointPowerLoc, JOINT_SPECULAR_POWER);
      gl.uniform1f(jointPullLoc, DEPTH_PULL_NDC);
      gl.bindBuffer(gl.ARRAY_BUFFER, jointBuffer);
      if (jointCapacity === 0) {
        gl.bufferData(gl.ARRAY_BUFFER, jointData, gl.DYNAMIC_DRAW);
        jointCapacity = jointData.length;
      }
      gl.bufferSubData(gl.ARRAY_BUFFER, 0, jointData, 0, jointFloats);
      gl.enableVertexAttribArray(0);
      gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 0, 0);
      gl.drawArrays(gl.POINTS, 0, jointFloats / 3);
    }
  }

  function markResize() {
    sizeDirty = true;
  }

  function dispose() {
    gl.deleteProgram(floorProgram);
    gl.deleteProgram(boneProgram);
    gl.deleteProgram(jointProgram);
    gl.deleteBuffer(floorBuffer);
    gl.deleteBuffer(boneBuffer);
    gl.deleteBuffer(jointBuffer);
  }

  return { draw, dispose, markResize };
}

const SkeletonStage = forwardRef(function SkeletonStage({ meta, pose, onOrbit }, ref) {
  const canvasRef = useRef(null);
  const rendererRef = useRef(null);
  const viewRef = useRef(null);
  const frameRef = useRef(0);
  const sceneRef = useRef(null);

  sceneRef.current = { meta, pose };

  if (meta && !viewRef.current) {
    viewRef.current = {
      azimuth: DEFAULT_AZIMUTH,
      elevation: DEFAULT_ELEVATION,
      distance: fitDistance(meta.floor),
      pan: { x: 0, y: 0, z: 0 },
    };
  }

  const drawCurrent = () => {
    const renderer = rendererRef.current;
    const scene = sceneRef.current;
    const view = viewRef.current;
    if (!renderer || !scene?.meta || !scene.pose || !view) return;
    renderer.draw(frameRef.current, {
      pose: scene.pose,
      jointCount: scene.meta.jointNames.length,
      bones: scene.meta.bones,
      boneColors: scene.meta.boneColors,
      floor: scene.meta.floor,
      azimuth: view.azimuth,
      elevation: view.elevation,
      distance: view.distance,
      pan: view.pan || { x: 0, y: 0, z: 0 },
    });
  };

  useImperativeHandle(ref, () => ({
    drawFrame(frame) {
      if (frame === frameRef.current && rendererRef.current) return;
      frameRef.current = frame;
      drawCurrent();
    },
  }));

  useEffect(() => {
    viewRef.current = meta
      ? {
          azimuth: DEFAULT_AZIMUTH,
          elevation: DEFAULT_ELEVATION,
          distance: fitDistance(meta.floor),
          pan: { x: 0, y: 0, z: 0 },
        }
      : null;
    frameRef.current = 0;
    drawCurrent();
  }, [meta]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const renderer = createRenderer(canvas);
    rendererRef.current = renderer;
    drawCurrent();

    const onResize = () => {
      if (renderer) renderer.markResize();
      drawCurrent();
    };
    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(onResize)
      : null;
    if (observer) observer.observe(canvas);
    window.addEventListener('resize', onResize);

    const onWheel = (event) => {
      if (!viewRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      viewRef.current.distance = zoomDistance(viewRef.current.distance, event.deltaY);
      drawCurrent();
      if (onOrbit) onOrbit();
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      canvas.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', onResize);
      if (observer) observer.disconnect();
      if (renderer) renderer.dispose();
      rendererRef.current = null;
    };
  }, [onOrbit]);

  const onPointerDown = (event) => {
    if (!viewRef.current) return;
    event.stopPropagation();
    if (typeof event.currentTarget.setPointerCapture === 'function') {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    const panMode = event.shiftKey || event.button === 1 || event.button === 2;
    event.currentTarget.dataset.dragMode = panMode ? 'pan' : 'orbit';
    event.currentTarget.dataset.dragX = String(event.clientX);
    event.currentTarget.dataset.dragY = String(event.clientY);
  };

  const onPointerMove = (event) => {
    const canvas = event.currentTarget;
    const captured = typeof canvas.hasPointerCapture === 'function'
      && canvas.hasPointerCapture(event.pointerId);
    if (!viewRef.current || !captured) return;
    const lastX = Number(canvas.dataset.dragX);
    const lastY = Number(canvas.dataset.dragY);
    const dx = event.clientX - lastX;
    const dy = event.clientY - lastY;
    if (canvas.dataset.dragMode === 'pan') {
      const scene = sceneRef.current;
      viewRef.current.pan = panTarget(
        viewRef.current.pan || { x: 0, y: 0, z: 0 },
        viewRef.current.azimuth,
        viewRef.current.elevation,
        viewRef.current.distance,
        dx,
        dy,
        Math.max(1, canvas.clientHeight),
        scene?.meta?.floor
      );
    } else {
      const next = orbitDrag(
        viewRef.current.azimuth,
        viewRef.current.elevation,
        dx,
        dy
      );
      viewRef.current.azimuth = next.azimuth;
      viewRef.current.elevation = clampElevation(next.elevation);
    }
    canvas.dataset.dragX = String(event.clientX);
    canvas.dataset.dragY = String(event.clientY);
    drawCurrent();
    if (onOrbit) onOrbit();
  };

  const onPointerUp = (event) => {
    const canvas = event.currentTarget;
    if (typeof canvas.hasPointerCapture === 'function'
      && canvas.hasPointerCapture(event.pointerId)
      && typeof canvas.releasePointerCapture === 'function') {
      canvas.releasePointerCapture(event.pointerId);
    }
    delete canvas.dataset.dragMode;
  };

  const onContextMenu = (event) => {
    event.preventDefault();
  };

  const axes = useMemo(() => {
    if (!meta || !pose) return null;
    return torsoAxes(
      pose,
      meta.jointNames,
      meta.jointNames.length,
      meta.frameCount
    );
  }, [meta, pose]);

  const snapPlane = (plane) => {
    if (!viewRef.current || !axes) return;
    const azimuth = planeViewAzimuth(axes, plane);
    if (!Number.isFinite(azimuth)) return;
    viewRef.current.azimuth = azimuth;
    viewRef.current.elevation = 0;
    viewRef.current.pan = { x: 0, y: 0, z: 0 };
    drawCurrent();
    if (onOrbit) onOrbit();
  };

  return (
    <div className="trial-live-skeleton" data-testid="trial-live-skeleton">
      <canvas
        ref={canvasRef}
        className="trial-live-canvas"
        data-testid="trial-live-canvas"
        aria-label="Drag to rotate, Shift+drag to pan, scroll to zoom"
        title="Drag to rotate, Shift+drag to pan, scroll to zoom"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onContextMenu={onContextMenu}
      />
      <div className="trial-live-view-buttons" role="group" aria-label="Camera plane views">
        <button
          type="button"
          className="trial-live-view-btn"
          data-testid="trial-live-view-sagittal"
          aria-label="Sagittal view"
          title="Sagittal view"
          disabled={!axes}
          onClick={() => snapPlane('sagittal')}
        >
          <Icon icon="mdi:walk" aria-hidden="true" />
        </button>
        <button
          type="button"
          className="trial-live-view-btn"
          data-testid="trial-live-view-frontal"
          aria-label="Frontal view"
          title="Frontal view"
          disabled={!axes}
          onClick={() => snapPlane('frontal')}
        >
          <Icon icon="mdi:human" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
});

export default SkeletonStage;
